/**
 * Core: Size guide
 * -----------------------------------------------------------------------------
 * Public API for the global size guide drawer, exposed as `cnvs.SizeGuide` for
 * third-party scripts to open a size guide for a product type other than the
 * one on the current page.
 *
 * - `set()` resolves the size guide collection metaobject and reports whether
 *   one exists, so a caller can decide whether to show its own trigger.
 * - `open()` and `close()` emit events handled by the size-guide-drawer
 *   component, which may not have mounted yet.
 */
import { convertToHandle } from '~/helpers/convert'
import { formatSizeGuide } from '~/helpers/format'
import { getVariables } from '~/helpers/graphql'

import sizeGuideQuery from '~graphql/sizeGuideMetaobject.gql'

export default function SizeGuide() {
  const cache = {}
  const state = {
    data: null,
    type: '',
  }

  /**
   * Set the product type the drawer should show a size guide for.
   * - Handles are cached so repeated calls don't re-query.
   * @param {String} type - Product type, or size guide collection handle.
   * @returns {Promise<Boolean>} Whether a size guide exists for the type.
   */
  async function set(type) {
    const handle = convertToHandle(type)

    if (!handle) {
      state.data = null
      state.type = ''
      return false
    }

    if (!Object.hasOwn(cache, handle)) {
      try {
        const response = await cnvs.Query({
          query: sizeGuideQuery,
          variables: getVariables({
            handle: {
              type: 'size_guide_collection',
              handle,
            },
          }),
        })

        cache[handle] = formatSizeGuide(response)
      } catch (error) {
        cnvs.ReportError('[Size Guide Query]', error)
        cache[handle] = null
      }
    }

    state.data = cache[handle]
    state.type = state.data ? handle : ''

    return Boolean(state.data)
  }

  /**
   * Open the size guide drawer.
   * - Does nothing if no size guide has been set, or if the drawer component
   *   has not mounted yet to listen for the event.
   * @returns {Boolean} Whether the drawer was opened.
   */
  function open() {
    if (!state.data) {
      return false
    }

    return cnvs.EventBus.emit('size-guide:open', state) !== false
  }

  /**
   * Close the size guide drawer.
   */
  function close() {
    cnvs.EventBus.emit('size-guide:close')
  }

  return {
    close,
    open,
    set,
    state,
  }
}
